import React, { useState } from 'react';
import { combineLatest, map } from 'rxjs';
import * as R from 'ramda';

import { positionsApi, protocolsApi } from 'api/modules';
import { PositionFilters } from 'api/types';
import { Loading, SectionWithTitle } from 'components';
import { useSubscribable } from 'utils/react';
import { calcFallbackedTotalUsd } from 'api/utils/calcFallbackedTotalUSD';
import { colors, styled } from 'core/styles';

import { PositionsTableView } from './PositionsTableView';
import { Context, Pagination, TableData } from './types';

type Data = {
  positions: TableData[];
  total: number;
};

type PositionsTableProps = {
  filters: PositionFilters;
  context: Context;
  isArchive: boolean;
  hideAmountColumns: boolean;
  title: string;
};

export function PositionsTable({
  filters,
  context,
  isArchive,
  hideAmountColumns,
  title,
}: PositionsTableProps) {
  const [pagination, setPagination] = useState<Pagination>({
    limit: 10,
    offset: 0,
  });

  const dataRD = useSubscribable<Data>(
    () =>
      combineLatest([
        positionsApi.getPositions$(
          {
            ...filters,
            ...pagination,
            is_archived: isArchive,
          },
          'latest',
        ),
        protocolsApi.getLendings$(true),
      ]).pipe(
        map(([{ data, total }, protocolsInfo]) => {
          const positions = data.map((position): TableData => {
            const protocolInfo = protocolsInfo.find(
              info =>
                info.network === position.network &&
                info.productSlug === position.productSlug &&
                info.versionSlug === position.versionSlug,
            );
            const market = protocolInfo?.marketByVid[position.marketVid];

            const filteredBalances = position.balances.filter(
              ({ balance }) =>
                !balance.collateral.isZero() || !balance.debt.isZero() || !balance.deposit.isZero(),
            );
            const tokens = R.uniqBy(
              ({ address }) => address,
              filteredBalances.map(({ token }) => token),
            );

            const depositTotalUSD = calcFallbackedTotalUsd(position.balances, 'deposit', true);
            const collateralTotalUSD = calcFallbackedTotalUsd(
              position.balances,
              'collateral',
              true,
            );
            const debtTotalUSD = calcFallbackedTotalUsd(position.balances, 'debt', true);

            return {
              productSlug: position.productSlug,
              versionSlug: position.versionSlug,
              productName: protocolInfo?.fullName || 'Unknown protocol',
              network: position.network,
              tokens,
              account: position.account,
              marketId: position.marketVid,
              balances: {
                deposit: depositTotalUSD,
                collateral: collateralTotalUSD,
                debt: debtTotalUSD,
              },
              metrics: position.metrics,
              lastActive: position.lastActive,
              marketName: market?.marketName || 'Unknown market',
              isMultiMarketProtocol: protocolInfo ? protocolInfo.isMultiMarket : false,
            };
          });

          return {
            positions,
            total,
          };
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [R.toString(filters), isArchive, pagination],
    { resetOnChangeTarget: false, clientOnly: true },
  );

  return (
    <SectionWithTitle title={title}>
      <Loading
        data={dataRD}
        progressProps={{
          height: '56px',
          variant: 'rounded',
          sx: { borderRadius: '16px' },
        }}
      >
        {({ positions, total }, loading) => {
          const table = (
            <PositionsTableView
              context={context}
              positions={positions}
              total={total}
              pagination={pagination}
              loading={loading}
              setPagination={setPagination}
              hideAmountColumns={hideAmountColumns}
            />
          );
          return total > 0 ? table : <Message>No active positions found</Message>;
        }}
      </Loading>
    </SectionWithTitle>
  );
}

const Message = styled('div')({
  width: '100%',
  padding: '16px 20px',
  background: colors.smokyWhite,
  borderRadius: 16,
  fontSize: 16,
});
